import React, { AriaAttributes } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconLookup } from '@fortawesome/fontawesome-common-types';
import classNames from 'classnames';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import * as styles from './Icon.module.scss';

export type IconType = IconLookup;
export type IconVariant =
  | 'warning'
  | 'neutral'
  | 'primary'
  | 'danger'
  | 'success'
  | undefined;
export type IconSize = 'xxxl' | 'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';
export type IconProps = {
  /**
   * Icon reference
   */
  icon: IconType;
  /**
   * Classes to be applied to the object
   */
  className?: string;
  size?: IconSize;
  variant?: IconVariant;
  /**
   * Whether to render the icon with inverse styling (white icon on colored background)
   */
  inverse?: boolean;
  weight?: 'normal' | 'bold';
  role?: 'img' | 'presentation';
  children?: string;
} & AriaAttributes;

export const Icon = ({
  icon,
  className,
  size,
  variant,
  inverse = false,
  weight,
  children,
  role = 'img',
  ...AriaProps
}: IconProps) => {
  const hasAriaText = Boolean(
    AriaProps['aria-label'] || AriaProps['aria-labelledby'] || children
  );
  const ariaLabel = AriaProps['aria-label'] || children;
  const applyAriaHidden = !(hasAriaText || AriaProps['aria-hidden']);

  const iconElement = (
    <FontAwesomeIcon
      role={role}
      icon={icon as IconProp}
      className={classNames(
        styles.Icon,
        className,
        size && styles[size],
        variant && styles[variant],
        inverse && styles.inverse,
        weight && styles[weight]
      )}
      aria-hidden={applyAriaHidden}
      aria-label={ariaLabel}
      {...AriaProps}
    />
  );

  // If inverse is true, wrap the white icon in a container with coloured background
  if (inverse) {
    return (
      <div
        className={classNames(styles.inverseWrapper, {
          [styles[`inverse-${variant}`]]: variant,
        })}
      >
        {iconElement}
      </div>
    );
  }

  return iconElement;
};
